import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

// API services
import { deleteUserAccount } from '../../../../api/services/accountDeletion';

// Redux actions
import { setSnackbarData, setSnackbarOpen } from '../../../snackbar/redux/snackbarSlice';
import { logoutUser } from '../../../user/redux/slices/userDataSlice';

// Icons
import { ReactComponent as AlertTriangleIcon } from '../../../../assets/icons/imported/alert-triangle.svg';
import { ReactComponent as XIcon } from '../../../../assets/icons/imported/x.svg';
import { ReactComponent as ShieldIcon } from '../../../../assets/icons/imported/shield.svg';
import { ReactComponent as FileTextIcon } from '../../../../assets/icons/imported/file-text.svg';
import { ReactComponent as HelpCircleIcon } from '../../../../assets/icons/imported/help-circle.svg';
import { ReactComponent as ChevronDownIcon } from '../../../../assets/icons/imported/chevron-down.svg';

// Styles
import Styles from './deleteAccountTab.module.css';

const DeleteAccountTab = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const userData = useSelector(state => state.userData.value)

    const [showConfirmation, setShowConfirmation] = useState(false)
    const [confirmationText, setConfirmationText] = useState('')
    const [selectedReason, setSelectedReason] = useState('')
    const [additionalComments, setAdditionalComments] = useState('')
    const [acknowledgeTerms, setAcknowledgeTerms] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)

    const deletionReasons = [
        'No longer need the service',
        'Found a better alternative',
        'Privacy concerns',
        'Technical issues',
        'Account security concerns',
        'Other'
    ]

    const handleDeleteAccount = async () => {
        if (confirmationText === 'DELETE MY ACCOUNT' && selectedReason && acknowledgeTerms) {
            setIsLoading(true)
            try {
                await deleteUserAccount(userData.uid, selectedReason)

                // Show success message
                dispatch(setSnackbarOpen(true))
                dispatch(setSnackbarData({
                    alertMessage: 'Account deletion request submitted successfully',
                    severity: 'success',
                    position: 'topRight'
                }))

                // Logout user after successful deletion
                dispatch(logoutUser(''))
                navigate('/markets')
            } catch (error) {
                console.error('Account deletion error:', error)
                dispatch(setSnackbarOpen(true))
                dispatch(setSnackbarData({
                    alertMessage: error.response?.data?.message || 'Failed to delete account. Please try again.',
                    severity: 'error',
                    position: 'topRight'
                }))
            } finally {
                setIsLoading(false)
            }
        }
    }

    const handleReasonSelect = (reason) => {
        setSelectedReason(reason)
        setIsDropdownOpen(false)
    }

    return (
        <div className={Styles.container}>
            <div className={Styles.card}>
                <div className={Styles.content}>
                    <div className={Styles.tabContainer}>
                        <h5>Account Deletion</h5>

                        <p className={Styles.subtitle}>
                            We're sorry to see you go. Please review the information below before proceeding.
                        </p>

                        {/* Warning Section */}
                        <div className={Styles.warningSection}>
                            <AlertTriangleIcon className={Styles.warningIcon} />
                            <div>
                                <h3 className={Styles.warningTitle}>
                                    Important: This action cannot be undone
                                </h3>
                                <p className={Styles.warningText}>
                                    Once you delete your account, all your data, transaction history, and wallet information will be permanently removed from our systems.
                                </p>
                            </div>
                        </div>

                        {/* What happens section */}
                        <div className={Styles.infoSection}>
                            <h3 className={Styles.sectionTitle}>
                                <FileTextIcon className={Styles.sectionIcon} />
                                What happens when you delete your account?
                            </h3>

                            <ul className={Styles.infoList}>
                                <li>All personal information and profile data will be permanently deleted</li>
                                <li>Transaction history and trading records will be removed</li>
                                <li>Any remaining balances must be withdrawn before deletion</li>
                                <li>Access to all platform features will be immediately revoked</li>
                                <li>You will not be able to recover your account or data</li>
                            </ul>
                        </div>

                        {/* Before you proceed section */}
                        <div className={Styles.infoSection}>
                            <h3 className={Styles.sectionTitle}>
                                <ShieldIcon className={Styles.sectionIcon} />
                                Before you proceed
                            </h3>

                            <div className={Styles.infoText}>
                                <p>Please ensure you have:</p>
                                <ul>
                                    <li>Withdrawn all funds from your wallets</li>
                                    <li>Downloaded any important transaction records</li>
                                    <li>Canceled any pending orders or transactions</li>
                                    <li>Removed all active beneficiaries</li>
                                </ul>
                            </div>
                        </div>

                        {!showConfirmation ? (
                            <div>
                                {/* Reason for deletion */}
                                <div className={Styles.formGroup}>
                                    <label className={Styles.label}>
                                        Reason for account deletion *
                                    </label>
                                    <div className={Styles.dropdownContainer}>
                                        <div
                                            className={Styles.dropdownButton}
                                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                        >
                                            <span className={selectedReason ? Styles.dropdownValue : Styles.dropdownPlaceholder}>
                                                {selectedReason || 'Select a reason'}
                                            </span>
                                            <ChevronDownIcon className={`${Styles.dropdownIcon} ${isDropdownOpen ? Styles.dropdownIconOpen : ''}`} />
                                        </div>
                                        {isDropdownOpen && (
                                            <div className={Styles.dropdownMenu}>
                                                {deletionReasons.map((reason, index) => (
                                                    <div
                                                        key={index}
                                                        className={`${Styles.dropdownOption} ${selectedReason === reason ? Styles.dropdownOptionSelected : ''}`}
                                                        onClick={() => handleReasonSelect(reason)}
                                                    >
                                                        {reason}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Additional comments */}
                                <div className={Styles.formGroup}>
                                    <label className={Styles.label}>
                                        Additional comments (optional)
                                    </label>
                                    <textarea
                                        value={additionalComments}
                                        onChange={(e) => setAdditionalComments(e.target.value)}
                                        placeholder="Help us improve by sharing your feedback..."
                                        rows={4}
                                        className={Styles.textarea}
                                    />
                                </div>

                                {/* Terms acknowledgment */}
                                <div className={Styles.termsSection}>
                                    <input
                                        type="checkbox"
                                        id="acknowledge"
                                        checked={acknowledgeTerms}
                                        onChange={(e) => setAcknowledgeTerms(e.target.checked)}
                                        className={Styles.checkbox}
                                    />
                                    <label htmlFor="acknowledge" className={Styles.termsLabel}>
                                        I understand that deleting my account is permanent and irreversible. I acknowledge that all my data, transaction history, and account information will be permanently deleted and cannot be recovered.
                                    </label>
                                </div>

                                {/* Action buttons */}
                                <div className={Styles.buttonContainer}>
                                    <button
                                        className={Styles.cancelButton}
                                        onClick={() => navigate('/account/profile')}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        onClick={() => setShowConfirmation(true)}
                                        disabled={!selectedReason || !acknowledgeTerms}
                                        className={`${Styles.deleteButton} ${(!selectedReason || !acknowledgeTerms) ? Styles.disabled : ''}`}
                                    >
                                        Continue with Deletion
                                    </button>
                                </div>
                            </div>
                        ) : (
                            /* Final confirmation modal */
                            <div className={Styles.confirmationSection}>
                                <div className={Styles.confirmationIcon}>
                                    <XIcon />
                                </div>

                                <h3 className={Styles.confirmationTitle}>
                                    Final Confirmation
                                </h3>

                                <p className={Styles.confirmationText}>
                                    This is your last chance to change your mind. To confirm account deletion,
                                    type <strong className={Styles.highlight}>DELETE MY ACCOUNT</strong> in the field below.
                                </p>

                                <input
                                    type="text"
                                    value={confirmationText}
                                    onChange={(e) => setConfirmationText(e.target.value)}
                                    placeholder="Type DELETE MY ACCOUNT"
                                    className={Styles.confirmationInput}
                                />

                                <div className={Styles.buttonContainer}>
                                    <button
                                        onClick={() => setShowConfirmation(false)}
                                        className={Styles.cancelButton}
                                    >
                                        Go Back
                                    </button>

                                    <button
                                        onClick={handleDeleteAccount}
                                        disabled={confirmationText !== 'DELETE MY ACCOUNT' || isLoading}
                                        className={`${Styles.deleteButton} ${(confirmationText !== 'DELETE MY ACCOUNT' || isLoading) ? Styles.disabled : ''}`}
                                    >
                                        {isLoading ? 'Processing...' : 'Delete My Account Forever'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Help section */}
                        <div className={Styles.helpSection}>
                            <HelpCircleIcon className={Styles.helpIcon} />
                            <p className={Styles.helpText}>
                                Need help or have questions about account deletion?
                            </p>
                            {/* <p className={Styles.contactText}>
                                Contact our support team at <a href="mailto:crypto@wealthtech.group" className={Styles.contactLink}>crypto@wealthtech.group</a> or call +356 2778 1148
                            </p> */}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default DeleteAccountTab 